import { forwardRef, useEffect, useRef, useState, ReactNode } from "react";
import { UnstyledButton, Group, Text, Tooltip, MantineSize } from "@mantine/core";
import classes from "./InlineRow.module.css";

interface InlineRowProps extends Omit<React.ComponentPropsWithoutRef<"button">, "value"> {
  label: string;
  value: string | null;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  labelWidth?: number | string;
  labelSize?: MantineSize;
  tooltip?: string | null;
  onValueChange?: (value: string) => void;
  suffix?: ReactNode;
}

export const InlineRow = forwardRef<HTMLButtonElement, InlineRowProps>(
  ({ label, value, placeholder = "Не выбран", required, disabled, labelWidth, labelSize, tooltip, onValueChange, suffix, className, onClick, ...others }, ref) => {
    const editable = !!onValueChange;
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value ?? "");
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (editing) { inputRef.current?.focus(); inputRef.current?.select(); }
    }, [editing]);

    function commit() { onValueChange?.(draft.trim()); setEditing(false); }
    function cancel() { setDraft(value ?? ""); setEditing(false); }

    const labelEl = (
      <Text fw={500} fz={labelSize} style={{ flexShrink: 0, width: labelWidth }}>
        {label}{required && <Text span c="red" ml={2}>*</Text>}
      </Text>
    );

    if (editable && editing) {
      return (
        <div className={[classes.row, classes.editing, className].filter(Boolean).join(" ")}>
          <Group wrap="nowrap" gap="md" style={{ width: "100%" }}>
            {labelEl}
            <Group gap={0} wrap="nowrap" style={{ minWidth: 0 }}>
              <div className={classes.gridInput}>
                <span className={classes.gridSizer} aria-hidden>{draft || " "}</span>
                <input
                  ref={inputRef}
                  className={classes.editInput}
                  value={draft}
                  onChange={e => setDraft(e.currentTarget.value)}
                  onBlur={commit}
                  onKeyDown={e => { if (e.key === "Enter") commit(); else if (e.key === "Escape") cancel(); }}
                />
              </div>
              {suffix && <Text c="dimmed" style={{ flexShrink: 0 }}>{suffix}</Text>}
            </Group>
          </Group>
        </div>
      );
    }

    const row = (
      <UnstyledButton
        ref={ref}
        {...others}
        onClick={editable ? () => { setDraft(value ?? ""); setEditing(true); } : onClick}
        className={[classes.row, className].filter(Boolean).join(" ")}
        data-disabled={disabled || undefined}
      >
        <Group justify={labelWidth ? "flex-start" : "space-between"} wrap="nowrap" gap="md" style={{ width: "100%" }}>
          {labelEl}
          <Group gap={0} wrap="nowrap" style={{ minWidth: 0 }}>
            <Text c={value ? undefined : "dimmed"} style={{ minWidth: 0 }} truncate>{value ?? placeholder}</Text>
            {suffix && value && <Text c="dimmed" style={{ flexShrink: 0 }}>{suffix}</Text>}
          </Group>
        </Group>
      </UnstyledButton>
    );
    return tooltip ? <Tooltip label={tooltip} multiline w={360} position="top-start" withArrow openDelay={300}>{row}</Tooltip> : row;
  }
);
